import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { auth } from "./firebaseConfig";
import { validUsername } from "./utils/pendingUsername";
import { canEditOwnName, saveOwnName } from "./utils/profileName";
import "./AccountSecurity.css";

// The profile name is the "username" chosen at signup and shown across the app.
// The backend refuses changes for teacher-managed student accounts; the
// button is only hidden here so those students are told who to ask instead.
const ProfileNameSection = ({ user }) => {
  const currentName = user?.username || user?.displayName || "";
  const [savedName, setSavedName] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    setSavedName(null);
    setIsEditing(false);
    setMessage(null);
  }, [user?.uid]);

  // Shows the saved name straight away; the profile listener catches up.
  const shownName = savedName ?? currentName;
  const editable = canEditOwnName(user);

  const startEditing = () => {
    setDraftName(shownName);
    setMessage(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (savingRef.current) return;
    setIsEditing(false);
    setMessage(null);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (savingRef.current) return;

    const name = validUsername(draftName);
    if (!name) {
      setMessage({
        type: "error",
        text: draftName.trim() ? "Names must be 100 characters or fewer." : "Please enter a name.",
      });
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    setMessage(null);
    try {
      const saved = await saveOwnName(name);
      setSavedName(saved || name);
      setIsEditing(false);
      setMessage({ type: "success", text: "Your name has been updated." });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Could not save your name. Please try again." });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <section className="account-security-section" aria-labelledby="profile-name-heading">
      <h2 id="profile-name-heading">Profile name</h2>
      {isEditing ? (
        <form onSubmit={handleSave} className="account-security-form" noValidate>
          <label htmlFor="profile-name">Name</label>
          <div className="account-security-input-row">
            <input
              id="profile-name"
              type="text"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              autoComplete="name"
              maxLength={100}
              readOnly={isSaving}
              aria-describedby={message ? "profile-name-message" : undefined}
            />
          </div>
          <div className="account-security-name-actions">
            <button
              type="button"
              className="account-security-secondary"
              onClick={cancelEditing}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="submit" className="account-security-submit" disabled={isSaving} aria-busy={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      ) : (
        <div className="account-security-name-row">
          <span className="account-security-name">{shownName || "No name set"}</span>
          {editable && (
            <button type="button" className="account-security-secondary" onClick={startEditing}>
              Edit name
            </button>
          )}
        </div>
      )}
      {!editable && (
        <p className="account-security-note">
          Your teacher manages the name on this account. Ask them if it needs correcting.
        </p>
      )}
      {message && (
        <div
          id="profile-name-message"
          className={`account-security-message ${message.type}`}
          role={message.type === "error" ? "alert" : "status"}
        >
          {message.text}
        </div>
      )}
    </section>
  );
};

const AccountSecurity = ({ user }) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");

  useEffect(() => {
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDarkMode);

    if (isDarkMode) {
      document.body.classList.add("dark-mode");
      document.documentElement.setAttribute("data-dark-mode", "true");
    } else {
      document.body.classList.remove("dark-mode");
      document.documentElement.setAttribute("data-dark-mode", "false");
    }
  }, []);

  const hasPasswordProvider = useMemo(() => {
    const providerData = auth.currentUser?.providerData || user?.providerData || [];
    return providerData.some((provider) => provider.providerId === "password");
  }, [user]);

  const passwordChecks = useMemo(() => [
    {
      label: "Current password entered",
      valid: currentPassword.length > 0,
    },
    {
      label: "At least 8 characters",
      valid: newPassword.length >= 8,
    },
    {
      label: "Includes a letter and a number",
      valid: /[A-Za-z]/.test(newPassword) && /\d/.test(newPassword),
    },
    {
      label: "Different from current password",
      valid: currentPassword.length > 0 && newPassword.length > 0 && newPassword !== currentPassword,
    },
    {
      label: "Passwords match",
      valid: newPassword.length > 0 && newPassword === confirmPassword,
    },
  ], [currentPassword, newPassword, confirmPassword]);

  const isPasswordReady = passwordChecks.every((check) => check.valid);

  const getFirebaseErrorMessage = (error) => {
    switch (error.code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
        return "Your current password is incorrect.";
      case "auth/weak-password":
        return "That new password is too weak. Choose a stronger password and try again.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a while before trying again.";
      case "auth/requires-recent-login":
        return "For security, please sign out, sign back in, and try changing your password again.";
      case "auth/network-request-failed":
        return "Network error. Check your connection and try again.";
      default:
        return error.message || "Could not change your password. Please try again.";
    }
  };

  const clearPasswordFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);

    if (!hasPasswordProvider) {
      setMessage({
        type: "error",
        text: "Password changes are not available for this account because you sign in with Google.",
      });
      return;
    }

    if (currentPassword && newPassword && newPassword === currentPassword) {
      setMessage({
        type: "error",
        text: "Your new password must be different from your current password.",
      });
      return;
    }

    if (!isPasswordReady) {
      setMessage({
        type: "error",
        text: "Please meet all password requirements before continuing.",
      });
      return;
    }

    const firebaseUser = auth.currentUser;
    if (!firebaseUser?.email) {
      setMessage({
        type: "error",
        text: "Your sign-in session could not be verified. Please sign in again.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      await updatePassword(firebaseUser, newPassword);

      clearPasswordFields();
      setMessage({
        type: "success",
        text: "Password changed successfully.",
      });
    } catch (error) {
      console.error("Error changing password:", error.code || error.message);
      setMessage({
        type: "error",
        text: getFirebaseErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const PasswordToggle = ({ visible, onClick, label }) => (
    <button
      type="button"
      className="account-security-icon-button"
      onClick={onClick}
      aria-label={visible ? `Hide ${label}` : `Show ${label}`}
      title={visible ? `Hide ${label}` : `Show ${label}`}
      disabled={isSubmitting}
    >
      {visible ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <main className={`account-security-page ${darkMode ? "account-security-dark" : ""}`}>
      <section className="account-security-shell">
        <button
          type="button"
          className="account-security-back"
          onClick={() => navigate("/dashboard")}
        >
          Back to Dashboard
        </button>

        <div className="account-security-card">
          <div className="account-security-icon">
            <ShieldCheck size={34} strokeWidth={1.8} />
          </div>

          <h1>Account & Security</h1>
          <p className="account-security-intro">
            Manage your profile name and sign-in password for StudyDen.
          </p>

          <ProfileNameSection user={user} />

          <h2 className="account-security-heading">Password</h2>

          {!hasPasswordProvider ? (
            <div className="account-security-provider-message" role="status">
              <LockKeyhole size={22} />
              <p>Password changes are not available for this account because you sign in with Google.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="account-security-form">
              <label htmlFor="current-password">Current password</label>
              <div className="account-security-input-row">
                <input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  required
                />
                <PasswordToggle
                  visible={showCurrentPassword}
                  onClick={() => setShowCurrentPassword((visible) => !visible)}
                  label="current password"
                />
              </div>

              <label htmlFor="new-account-password">New password</label>
              <div className="account-security-input-row">
                <input
                  id="new-account-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  required
                />
                <PasswordToggle
                  visible={showNewPassword}
                  onClick={() => setShowNewPassword((visible) => !visible)}
                  label="new password"
                />
              </div>

              <label htmlFor="confirm-account-password">Confirm new password</label>
              <div className="account-security-input-row">
                <input
                  id="confirm-account-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  required
                />
                <PasswordToggle
                  visible={showConfirmPassword}
                  onClick={() => setShowConfirmPassword((visible) => !visible)}
                  label="confirmation password"
                />
              </div>

              <ul className="account-security-checks" aria-label="Password requirements">
                {passwordChecks.map((check) => (
                  <li key={check.label} className={check.valid ? "valid" : ""}>
                    <span aria-hidden="true">{check.valid ? "OK" : "-"}</span>
                    {check.label}
                  </li>
                ))}
              </ul>

              {message && (
                <div className={`account-security-message ${message.type}`} role="alert">
                  {message.text}
                </div>
              )}

              <button
                type="submit"
                className="account-security-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Changing Password..." : "Change Password"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default AccountSecurity;
